import { useState } from 'react';
import {
  FAQCard,
  faqIconBoxClassName,
  faqRowClassName,
  faqRowWrapperClassName,
  faqTitleSizeClassName,
} from './FAQ-card';
import { daFaqList, faqList } from './FAQ.mock';
import { withErrorBoundary } from '../../lib/HOC/withErrorBoundary/withErrorBoundary';
import { useTenant } from 'lib/hooks/useTenant';
import { Image } from 'components/Image/Image';
import {
  TripFaq,
  useTripFaqsQuery,
} from 'lib/react-query/Queries/useTripFaqsQuery';

type FaqItem = { id: number; title: string; context: string };

// Same markup the website wraps product page answers in.
const toFaqItems = (tripFaqs: TripFaq[]): FaqItem[] => {
  const seen = new Set<number>();
  return tripFaqs.reduce<FaqItem[]>((items, { id, question, answer }) => {
    if (seen.has(id)) return items;
    seen.add(id);
    items.push({
      id,
      title: question,
      context: `<p class="whitespace-pre-line">${answer}</p>`,
    });
    return items;
  }, []);
};

// Same row count as the general list; the trip list length is unknown while
// loading. Widths vary so the rows read like real questions.
const FAQ_PLACEHOLDER_TITLE_WIDTHS = [
  '70%',
  '55%',
  '82%',
  '62%',
  '75%',
  '48%',
  '66%',
];

// Built from the same class names as FAQCard (row wrapper, button row, title
// and icon box) so the placeholder has the exact geometry of the final list.
const FAQLoading = () => (
  <div aria-hidden="true" data-testid="faq-loading">
    {FAQ_PLACEHOLDER_TITLE_WIDTHS.map((width, i) => (
      <div
        key={i}
        data-testid="faq-loading-row"
        className={faqRowWrapperClassName(
          i !== FAQ_PLACEHOLDER_TITLE_WIDTHS.length - 1
        )}
      >
        <div data-testid="faq-loading-button-row" className={faqRowClassName}>
          <span
            data-testid="faq-loading-title-box"
            className={`${faqTitleSizeClassName} flex h-5 sm:h-6 items-center`}
            style={{ width }}
          >
            <span
              data-testid="faq-loading-title"
              className="block h-3.5 sm:h-4 w-full rounded-full bg-light-800 animate-pulse motion-reduce:animate-none"
            ></span>
          </span>
          <span className={faqIconBoxClassName}>
            <span
              data-testid="faq-loading-icon"
              className="block h-4 w-4 rounded-sm bg-light-800 animate-pulse motion-reduce:animate-none"
            ></span>
          </span>
        </div>
      </div>
    ))}
  </div>
);

type FAQViewProps = {
  tripFaqs?: TripFaq[];
  isLoading?: boolean;
};

function FAQView({ tripFaqs, isLoading = false }: FAQViewProps) {
  const [index, setIndex] = useState(-1);
  const { isDA } = useTenant();
  const tripFaqItems = !isDA && tripFaqs ? toFaqItems(tripFaqs) : [];
  const hasTripFaqs = tripFaqItems.length > 0;
  const faqs: FaqItem[] = isDA
    ? daFaqList
    : hasTripFaqs
    ? tripFaqItems
    : faqList;
  const idPrefix = isDA ? 'dealsaway' : hasTripFaqs ? 'gwat-trip' : 'gwat';

  return (
    <div
      className="p-5 sm:p-10 border border-light-800 md:rounded-md bg-light-600"
      id="faq"
    >
      <h2
        className={`text-2xl sm:text-3xl  leading-[1.875rem] font-bold ${
          isDA ? ' text-dark-800' : ' text-teal-900'
        }`}
      >
        FAQ
      </h2>
      <div className="mt-5 mb-0 p-3 rounded-md border border-yellow-900 bg-[#fff1d34d] text-xs font-semibold text-dark-700 flex items-center">
        <div className="w-6 h-6 mr-[0.625rem]">
          <Image path="booking/icons/content.svg" alt="content icon" />
        </div>
        Looking for some answers? Here's some of our top FAQ's.
      </div>
      {isLoading && <FAQLoading />}
      {!isLoading &&
        faqs.map((item, i) => (
          <FAQCard
            key={item.id}
            id={`faq-${idPrefix}-${item.id}`}
            title={item.title}
            context={item.context}
            isExpand={item.id === index}
            click={() => {
              item.id === index ? setIndex(-1) : setIndex(item.id);
            }}
            hasBorder={faqs.length - 1 !== i}
          />
        ))}
    </div>
  );
}

// The only place the trip FAQs hook (and so the trip query, store and query
// client) is used, so trees without those providers can render FAQ with
// `generalOnly` and never touch it.
function ConnectedFAQView() {
  const { faqs, isLoading } = useTripFaqsQuery();
  return <FAQView tripFaqs={faqs} isLoading={isLoading} />;
}

type FAQProps = {
  // Render the general list only, with no trip lookup. For trees that have no
  // Redux store or React Query client (the /flight-demo UI demo, Storybook).
  generalOnly?: boolean;
};

function FAQComponent({ generalOnly = false }: FAQProps) {
  return generalOnly ? <FAQView /> : <ConnectedFAQView />;
}

export const FAQ = withErrorBoundary({ Component: FAQComponent });
